import React, { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { useOnClickOutside } from '@/modules/Search/hooks/useOnClickOutside';
import { SearchCategory } from '@/modules/Search/types';
import { SEARCH_CATEGORIES } from '@/modules/Search/utils';
import { cx } from 'class-variance-authority';

import SvgArrow from '@/components/common/Icons/Arrow';
import SvgCheck from '@/components/common/Icons/Check';
import { ContentSearchType } from '@/components/common/Providers/GraphqlProvider/generated';

interface SearchCategorySelectorProps {
  value?: ContentSearchType[];
  onChange?: (value: ContentSearchType[]) => void;
}

export default function SearchCategorySelector({
  value: selectedIds = [],
  onChange,
}: SearchCategorySelectorProps) {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const selectedLabels = useMemo(() => {
    return SEARCH_CATEGORIES.filter((cat) => selectedIds.includes(cat.id)).map((cat) => cat.label);
  }, [selectedIds]);

  const displayValue = useMemo(() => {
    if (selectedLabels.length === 0) return 'General';
    if (selectedLabels.length === 1) return selectedLabels[0];
    return `Selecciones: (${selectedLabels.length})`;
  }, [selectedLabels]);

  const toggleOpen = () => {
    setOpen((prev) => !prev);
    if (!open) {
      setFocusedIndex(-1);
    }
  };

  const handleSearchCategoryToggle = (searchCategory: SearchCategory) => {
    const isSelected = selectedIds.includes(searchCategory.id);
    const newValue = isSelected
      ? selectedIds.filter((id) => id !== searchCategory.id)
      : [...selectedIds, searchCategory.id];

    onChange?.(newValue);
    setOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev < SEARCH_CATEGORIES.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : SEARCH_CATEGORIES.length - 1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0) {
          handleSearchCategoryToggle(SEARCH_CATEGORIES[focusedIndex]);
        }
        break;
      case 'Escape':
      case 'Tab':
        setOpen(false);
        triggerRef.current?.focus();
        break;
      default:
        break;
    }
  };

  useOnClickOutside(containerRef, () => setOpen(false));

  return (
    <div className="relative hidden lg:block" ref={containerRef}>
      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="type-select-listbox"
        aria-label="Seleccionar tipo de búsqueda"
        tabIndex={0}
        onClick={toggleOpen}
        onKeyDown={handleKeyDown}
        className="flex h-full w-44 cursor-pointer items-center justify-between rounded-l-3xl bg-primaryLight px-4 py-3 font-normal text-primary outline-none focus-visible:ring-2 focus-visible:ring-blueDark/20"
      >
        <span className="overflow-hidden text-ellipsis whitespace-nowrap font-workSans text-sm">
          {displayValue}
        </span>
        <div className={cx('transition-all', open ? 'rotate-[270deg]' : 'rotate-90')}>
          <SvgArrow width={16} height={16} />
        </div>
      </div>

      {open && (
        <div
          id="type-select-listbox"
          role="listbox"
          aria-multiselectable="true"
          ref={listboxRef}
          style={{ zIndex: 'var(--z-dropdown)' }}
          className="absolute left-0 top-0 w-full overflow-hidden border border-blueLight/30 bg-white shadow-xl"
        >
          {SEARCH_CATEGORIES.map((searchCategory, index) => {
            const isSelected = selectedIds.includes(searchCategory.id);
            const isFocused = index === focusedIndex;
            return (
              <div
                key={searchCategory.id}
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                onClick={() => handleSearchCategoryToggle(searchCategory)}
                className={cx(
                  'flex cursor-pointer items-center justify-between border-b border-gray-100 px-4 py-3 outline-none transition-colors last:border-0',
                  isSelected
                    ? 'bg-primary text-white hover:bg-primary'
                    : 'text-[#4B4B4B] hover:bg-gray-50',
                  isFocused && !isSelected && 'bg-gray-100',
                  isFocused && isSelected && 'ring-2 ring-inset ring-white/30'
                )}
              >
                <span className="font-workSans text-sm font-medium">{searchCategory.label}</span>
                {isSelected && (
                  <div className="text-white">
                    <SvgCheck width={18} height={18} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
